/* eslint-disable prefer-arrow-callback */
import React, { useEffect, useRef, useState } from "react";

import { productAnalytics, useGenerateCmcMixCopy, useGetSoilSightCmcRecommendation, useTranslation } from "utils";

import { CmcMixCopyRequest, CmcScenarioRecommendation, SoilSightCropRecommendationItem } from "models";

import { AntModal, Button, Flex, Icon, Progress, Skeleton, Table, Tooltip, Typography } from "components";

import { RecommenderComputing } from "pages/app/scenario-analysis/components/RecommenderComputing";
import { getCoverCropMixKey } from "pages/app/scenario-analysis/utils/soilSightTracking";

type CoverCropCtaButtonProps = {
  onClick: (event: React.MouseEvent) => void;
};

type CoverCropRecommenderOverlayProps = {
  open: boolean;
  onClose: () => void;
  scenarioId: string | undefined;
  mainCropLabelLookup: Record<string, string>;
  mainCropIds?: string[];
  onApply: (slot: CmcScenarioRecommendation, legumeShare: number) => void;
};

const computeMatchScore = (recommendations: SoilSightCropRecommendationItem[]): number => {
  const components = recommendations.filter((r) => r.weightShare > 0);
  if (components.length === 0) return 0;
  const totalWeight = components.reduce((sum, r) => sum + r.weightShare, 0);
  if (totalWeight === 0) return 0;
  const weighted = components.reduce((sum, r) => sum + r.points * r.weightShare, 0);
  return Math.round(weighted / totalWeight);
};

const computeLegumeShare = (recommendations: SoilSightCropRecommendationItem[]): number => {
  const components = recommendations.filter((r) => r.weightShare > 0);
  const totalWeight = components.reduce((sum, r) => sum + r.weightShare, 0);
  if (totalWeight === 0) return 0;
  const legumeWeight = components.filter((r) => r.isLegume).reduce((sum, r) => sum + r.weightShare, 0);
  return Math.round((legumeWeight / totalWeight) * 100);
};

export const CoverCropCtaButton = ({ onClick }: CoverCropCtaButtonProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    <Button
      className="scenario-analysis-cover-crop-cta"
      type="secondary"
      size="middle"
      onClick={onClick}
      iconLeft
      icon={<Icon iconName="klim/plant-ai" size={20} />}
    >
      <Tooltip title={t("scenarioBuilder.coverCropAdvice")} placement="top" className="scenario-analysis-truncating-label">
        {t("scenarioBuilder.coverCropAdvice")}
      </Tooltip>
    </Button>
  );
};

type MixCardProps = {
  slot: CmcScenarioRecommendation;
  applied: boolean;
  onApply: () => void;
  aiName: string | null;
  aiRationale: string | null;
  aiLoading: boolean;
};

const RATIONALE_TRUNCATE_AT = 160;

const MixCard = ({ slot, applied, onApply, aiName, aiRationale, aiLoading }: MixCardProps) => {
  const { t } = useTranslation(["scenarioAnalysis", "practices", "cropMixComposer"]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const components = slot.recommendations.filter((r) => r.weightShare > 0);
  const matchScore = computeMatchScore(slot.recommendations);
  const legumeShare = computeLegumeShare(slot.recommendations);
  const speciesCount = components.length;

  const sortedTopGoals = slot.goalScores.sort((a, b) => b.score - a.score).slice(0, 6);

  const shouldTruncate = (aiRationale?.length ?? 0) > RATIONALE_TRUNCATE_AT;
  const truncatedText = shouldTruncate && !descExpanded ? `${aiRationale!.slice(0, aiRationale!.lastIndexOf(" ", RATIONALE_TRUNCATE_AT))}...` : aiRationale;

  return (
    <div className="mix-card">
      <Flex className="mix-card-header" justify="space-between" gap="1rem">
        <Flex className="mix-card-header-left" vertical gap="0.5rem">
          {aiLoading ? (
            <Skeleton type="title" width="60%" />
          ) : (
            <Typography variant="paragraph-l" className="mix-card-name mix-card-name--bold">
              {aiName ?? t("scenarioAnalysis:mixCard.fallbackName")}
            </Typography>
          )}
          <Typography variant="paragraph-s" color="grey-600" className="mix-card-meta">
            {t("scenarioAnalysis:mixCard.speciesLegumes", { count: speciesCount, pct: legumeShare })}
          </Typography>
          {aiLoading ? (
            <Skeleton type="paragraph" rows={2} />
          ) : aiRationale ? (
            <div className="mix-card-description">
              <Typography key={`mix-card-description-truncated-${shouldTruncate && !descExpanded}`} variant="paragraph-m" color="grey-600">
                {truncatedText}
                {shouldTruncate && !descExpanded && (
                  <Typography variant="paragraph-s" color="grey-600" className="mix-card-description-toggle" onClick={() => setDescExpanded(true)}>
                    {t("scenarioAnalysis:mixCard.more").toUpperCase()}
                  </Typography>
                )}
              </Typography>
              {shouldTruncate && descExpanded && (
                <Typography variant="paragraph-s" color="grey-600" className="mix-card-description-toggle" onClick={() => setDescExpanded(false)}>
                  {t("scenarioAnalysis:mixCard.less").toUpperCase()}
                </Typography>
              )}
            </div>
          ) : null}
          <Flex className="mix-card-chips" gap="0.25rem">
            {components.map((r) => (
              <Typography key={r.coverCropId} variant="paragraph-s" className="mix-card-chip">
                {t(`practices:crops.${r.coverCropTranslationSlug}`)}
              </Typography>
            ))}
          </Flex>
          <Flex className="mix-card-strengths" gap="1rem">
            {sortedTopGoals.slice(0, 2).map(({ goal }) => (
              <Flex key={goal} gap="0.25rem" className="mix-card-strength" align="center">
                <Icon iconName="tabler/arrow-up-right-circle" size="s" color="#6d6d6d" className="mix-card-strength-icon" aria-hidden />
                <Typography variant="paragraph-s" color="grey-600">
                  {t(`cropMixComposer:goals.${goal}`)}
                </Typography>
              </Flex>
            ))}
          </Flex>
        </Flex>
        <Flex className="mix-card-header-right" vertical align="flex-end">
          <Typography variant="h2" className="mix-card-score-value">
            {`${matchScore}%`}
          </Typography>
          <Typography variant="paragraph-s" color="grey-600" className="mix-card-score-label">
            {t("scenarioAnalysis:mixCard.matchLabel")}
          </Typography>
        </Flex>
      </Flex>
      <div className="mix-card-divider" />
      <Flex className="mix-card-actions-container" justify="flex-end" gap="1rem">
        <Button type="text" className="mix-card-details-btn" onClick={() => setDetailsOpen((prev) => !prev)}>
          {t("scenarioAnalysis:mixCard.details")}
          <Icon iconName={detailsOpen ? "tabler/chevron-up" : "tabler/chevron-down"} size="s" />
        </Button>
        {applied ? (
          <Flex className="mix-card-applied-btn" gap="0.25rem" justify="center" align="center">
            <Typography variant="paragraph-m" color="green-400" bold>
              {t("scenarioAnalysis:mixCard.applied")}
            </Typography>
            <Icon iconName="tabler/check" size="m-light" color="#065c45" />
          </Flex>
        ) : (
          <Button type="primary" className="mix-card-apply-btn" onClick={onApply} disabled={applied}>
            {t("scenarioAnalysis:mixCard.apply")}
          </Button>
        )}
      </Flex>
      {detailsOpen && (
        <>
          <div className="mix-card-divider" />
          <Flex className="mix-card-details" vertical>
            <Flex className="mix-card-criteria-scores-list" vertical gap="1rem">
              <Typography variant="paragraph-s" bold>
                {t("scenarioAnalysis:mixCard.criteriaScores")?.toUpperCase()}
              </Typography>
              <Flex vertical gap="0.75rem">
                {sortedTopGoals.map(({ goal, score }) => (
                  <div key={goal} className="mix-card-criteria-score-grid">
                    <Typography variant="paragraph-s">{t(`cropMixComposer:goals.${goal}`)}</Typography>
                    <Progress percent={score} showInfo={false} className="mix-card-criteria-progress" />
                    {/* the score is returned on a 0-100 scale, we need to show it on a 0-5 scale */}
                    <Typography variant="paragraph-s">{(score / 10 / 2).toFixed(1)}</Typography>
                  </div>
                ))}
              </Flex>
            </Flex>
            <Flex className="mix-card-species-list" vertical>
              <Typography variant="paragraph-s" bold>
                {t("scenarioAnalysis:mixCard.speciesInMix")?.toUpperCase()}
              </Typography>
              <Table
                data={components}
                rowKey="coverCropId"
                pagination={false}
                style={{ width: "100%" }}
                columns={[
                  {
                    title: t("scenarioAnalysis:mixCard.speciesColumn"),
                    dataIndex: "coverCropTranslationSlug",
                    render: (slug: string) => <Typography variant="paragraph-s">{t(`practices:crops.${slug}`)}</Typography>,
                  },
                  {
                    title: t("scenarioAnalysis:mixCard.familyColumn"),
                    dataIndex: "cropFamily",
                    render: (family: string) => (
                      <Typography variant="paragraph-s" color="grey-600">
                        {t(`cropMixComposer:plantFamilies.${family.toLowerCase()}`)}
                      </Typography>
                    ),
                  },
                  {
                    title: t("scenarioAnalysis:mixCard.shareColumn"),
                    dataIndex: "weightShare",
                    align: "right",
                    render: (val: number) => <Typography variant="paragraph-s">{`${val.toFixed(1)}%`}</Typography>,
                  },
                  {
                    title: t("scenarioAnalysis:mixCard.seedingRateColumn"),
                    dataIndex: "weightSeedingRate",
                    align: "right",
                    render: (val: number) => <Typography variant="paragraph-s">{`${val.toFixed(2)} kg/ha`}</Typography>,
                  },
                ]}
              />
            </Flex>
          </Flex>
        </>
      )}
    </div>
  );
};

export const CoverCropRecommenderOverlay = ({ open, onClose, scenarioId, mainCropLabelLookup, mainCropIds, onApply }: CoverCropRecommenderOverlayProps) => {
  const { t, i18n } = useTranslation("scenarioAnalysis");
  const [appliedSequences, setAppliedSequences] = useState<Set<number>>(new Set());
  const [hasCycled, setHasCycled] = useState(false);

  useEffect(() => {
    if (!open) setHasCycled(false);
  }, [open]);

  const { data: recommendations, isLoading } = useGetSoilSightCmcRecommendation(open ? scenarioId : undefined, mainCropIds);
  const showResults = hasCycled && !isLoading;

  // We report once per opening. An effect, not onSuccess, so a cached answer also counts.
  const hasTrackedViewRef = useRef(false);
  useEffect(
    function trackRecommendationView() {
      if (!open) {
        hasTrackedViewRef.current = false;
        return;
      }
      if (!showResults || !recommendations || hasTrackedViewRef.current) return;
      hasTrackedViewRef.current = true;
      productAnalytics.track("soilsights:cover_crop_recommendation_view", {
        year_indexes: recommendations.map((slot) => slot.yearIndex),
        slot_count: recommendations.length,
      });
    },
    [open, showResults, recommendations]
  );

  const cropName = (cropId: string) => mainCropLabelLookup[cropId] ?? cropId;

  const aiRequest: CmcMixCopyRequest | null =
    showResults && recommendations
      ? {
          language: i18n.language,
          sequences: recommendations.map((slot) => ({
            yearIndex: slot.yearIndex,
            predecessorCrop: cropName(slot.crop1Id),
            successorCrop: cropName(slot.crop2Id),
            legumeShare: computeLegumeShare(slot.recommendations),
            matchScore: computeMatchScore(slot.recommendations),
            components: slot.recommendations
              .filter((r) => r.weightShare > 0)
              .map((r) => ({ name: t(`practices:crops.${r.coverCropTranslationSlug}`), weightShare: r.weightShare, isLegume: r.isLegume })),
            criteriaScores: slot.goalScores.map(({ goal, score }) => ({ goal, score })),
            topStrengths: slot.goalScores
              .slice()
              .sort((a, b) => b.score - a.score)
              .slice(0, 2)
              .map(({ goal }) => goal),
          })),
        }
      : null;

  const { data: aiCopyData, isLoading: aiLoading } = useGenerateCmcMixCopy(aiRequest);
  const aiCopyByYearIndex = Object.fromEntries((aiCopyData ?? []).map((item) => [item.yearIndex, item]));

  const handleApply = (slot: CmcScenarioRecommendation) => {
    const legumeShare = slot.legumeExcluded ? 0 : computeLegumeShare(slot.recommendations);
    productAnalytics.track("soilsights:cover_crop_mix_add", {
      mix_type: getCoverCropMixKey(slot.mixType),
      legume_share: legumeShare,
      year_index: slot.yearIndex,
    });
    onApply(slot, legumeShare);
    setAppliedSequences((prev) => new Set([...prev, slot.yearIndex]));
  };

  const handleClose = () => {
    setAppliedSequences(new Set());
    onClose();
  };

  return (
    <AntModal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
      centered
      closeIcon={<Icon iconName="tabler/x" size="m" />}
      className="soil-sight-cover-crop-recommender-overlay"
      title={
        <Flex align="center" gap="0.5rem" className="cover-crop-recommender-header">
          <Icon iconName="klim/plant-ai" size="m" />
          <Typography variant="h3">{t("recommenderOverlay.title")}</Typography>
        </Flex>
      }
    >
      {!showResults ? (
        <div className="cover-crop-recommender-loading">
          <RecommenderComputing onCycleComplete={() => setHasCycled(true)} />
        </div>
      ) : (
        <Flex className="cover-crop-recommender-body" vertical gap="3rem">
          {(recommendations ?? []).map((slot) => {
            const hasRecommendations = slot.recommendations.some((r) => r.weightShare > 0);
            return (
              <div key={slot.yearIndex} className="cover-crop-sequence-section">
                <Flex align="center" gap="0.5rem" className="cover-crop-sequence-breadcrumb">
                  <Typography variant="paragraph-m">{cropName(slot.crop1Id)}</Typography>
                  <Icon iconName="tabler/arrow-right" size="s" color="#6d6d6d" />
                  <Icon iconName="klim/plant-ai" size="m-light" color="#6d6d6d" />
                  <Icon iconName="tabler/arrow-right" size="s" color="#6d6d6d" />
                  <Typography variant="paragraph-m">{cropName(slot.crop2Id)}</Typography>
                </Flex>
                {hasRecommendations ? (
                  <MixCard
                    slot={slot}
                    applied={appliedSequences.has(slot.yearIndex)}
                    onApply={() => handleApply(slot)}
                    aiName={aiCopyByYearIndex[slot.yearIndex]?.name ?? null}
                    aiRationale={aiCopyByYearIndex[slot.yearIndex]?.rationale ?? null}
                    aiLoading={aiLoading}
                  />
                ) : (
                  <Flex className="mix-card" vertical justify="center" align="center" gap="0.5rem">
                    <Typography variant="paragraph-m" color="grey-600" bold className="cover-crop-no-recommendations">
                      {t("recommenderOverlay.noRecommendationsTitle")}
                    </Typography>
                    <Typography variant="paragraph-s" color="grey-600" className="cover-crop-no-recommendations">
                      {t("recommenderOverlay.noRecommendationsDescription")}
                    </Typography>
                  </Flex>
                )}
              </div>
            );
          })}
        </Flex>
      )}
    </AntModal>
  );
};

export default CoverCropRecommenderOverlay;
